import produce from 'immer';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useError } from '../Components/ErrorBoundary';
import { CancelledRequest, HttpExecutorConfig } from '../Shared/HttpRequestExecutor';
import { Nullable } from '../Shared/Types';
import { useDebounce } from './useDebounce';

type FetchArguments = Array<string | number | boolean | object | undefined | null>;

type Config<PayloadType> = {
    loadOnInit?: FetchArguments;
    defaultValue?: PayloadType;
    useCache?: boolean;
    httpConfig: HttpExecutorConfig;
    getApi(config: HttpExecutorConfig, ...params: FetchArguments): Promise<PayloadType | CancelledRequest>;
};

type State<PayloadType> = {
    data: Nullable<PayloadType>;
    status: UseAsyncStatus;
    loadCount: number;
};

export type UseAsyncReturnOperations<PayloadType> = State<PayloadType> & {
    debouncedGetApi(...params: FetchArguments): Promise<void>;
    getApi(...params: FetchArguments): Promise<void>;
    setData(data: PayloadType): void;
    updateData(action: (data: PayloadType) => PayloadType): void;
    reset(): void;
};

export enum UseAsyncStatus {
    Init,
    Loading, // Used during first load
    Loaded,
    PermissionError,
    Reloading // Used for every following load
}

type Actions =
    | { type: 'Reset'; data: any }
    | { type: 'Loading' }
    | { type: 'Loaded'; data: any }
    | { type: 'SetData'; data: any };

const asyncReducer = produce((state, action: Actions) => {
    switch (action.type) {
        case 'Reset':
            state.status = UseAsyncStatus.Init;
            state.loadCount = 0;
            state.error = '';
            state.data = action.data;
            break;
        case 'Loading':
            state.status = state.loadCount === 0 ? UseAsyncStatus.Loading : UseAsyncStatus.Reloading;
            state.error = '';
            break;
        case 'Loaded':
            state.status = UseAsyncStatus.Loaded;
            state.loadCount = state.loadCount + 1;
            state.error = '';
            state.data = action.data;
            break;
        case 'SetData':
            state.data = action.data;
            break;
    }
});

function paramsToString(...params: any): string {
    return params
        .map((it: any) => {
            if (typeof it === 'object') return JSON.stringify(it);

            return it.toString();
        })
        .join('-');
}

export function useAsync<PayloadType>(config: Config<PayloadType>): UseAsyncReturnOperations<PayloadType> {
    const throwError = useError();
    const cacheRef = useRef(new Map<string, any>());
    const cache = cacheRef.current;
    const requestCountRef = useRef(0);

    const initialState: State<PayloadType> = {
        loadCount: 0,
        status: UseAsyncStatus.Init,
        data: config.defaultValue !== undefined ? config.defaultValue : null
    };

    function reset(): void {
        requestCountRef.current = requestCountRef.current + 1;
        dispatch({ type: 'Reset', data: config.defaultValue !== undefined ? config.defaultValue : null });
    }

    const [asyncState, dispatch] = useReducer(asyncReducer, initialState);
    // Used for instant query
    async function getApi(...params: FetchArguments): Promise<void> {
        requestCountRef.current = requestCountRef.current + 1;
        const currentRequestCount = requestCountRef.current;
        const key = paramsToString(...params);
        dispatch({ type: 'Loading' });

        if (config.useCache && cache.has(key)) {
            dispatch({ type: 'Loaded', data: cache.get(key) });
        } else {
            config.getApi(config.httpConfig, ...params).then((newData: PayloadType | CancelledRequest) => {
                if (newData instanceof CancelledRequest) return;

                if (requestCountRef.current === currentRequestCount) {
                    // Caching might cause memory leak. Watch this.
                    if (config.useCache) {
                        cache.set(key, newData);
                    }
                    dispatch({ type: 'Loaded', data: newData });
                }
            }, throwError);
        }
    }

    const [searchArguments, setSearchArguments] = useState<Nullable<FetchArguments>>(null);

    // Effect for calling load on init
    useEffect(() => {
        if (!config.loadOnInit) return;
        getApi(...config.loadOnInit);
    }, []);

    function debouncedGetApi(...newFetchArguments: FetchArguments[]) {
        setSearchArguments(newFetchArguments);
    }

    const debouncedSearchArguments = useDebounce(searchArguments);

    useEffect(() => {
        if (debouncedSearchArguments) getApi(...debouncedSearchArguments);
    }, [debouncedSearchArguments]);

    const dataRef = useRef(asyncState);
    dataRef.current = asyncState.data;
    const updateData = (action: (data: PayloadType) => PayloadType) => {
        dispatch({ type: 'SetData', data: action(dataRef.current) });
    };

    function setData(data: PayloadType) {
        dispatch({ type: 'SetData', data });
    }

    return {
        ...asyncState,
        getApi,
        debouncedGetApi,
        setData,
        updateData,
        reset
    };
}
